import { Input, notification } from 'antd';
import { ChangeEvent, Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import getHEOnboardingProfile from '../../api/getHEOnboardingProfile';
import setHEOnboardingProfile from '../../api/setHEOnboardingProfile';
import { GO_TO_APPOINTMENTS, GO_TO_HE_ONBOARDING } from '../../routes/paths';
import { getSelectedClinicId } from '../../store/idbHandler';
import { track } from '../../tracking';
import { captureError, captureErrorString } from '../captureError';
import { HEOnboardingState, heOnboardingStepsArray } from './HEOnboarding';
import { FetchingLoader, Footer, Header } from './Utils';

interface State {
    isFetchingProfile?: boolean;
    pancard?: string;
    isSavingProfile?: boolean;
}

type Props = RouteComponentProps;

class FinishPage extends PureComponent<Props, State> {
    state: State = {
        isFetchingProfile: true,
    };
    currentIndex = 2;

    componentDidMount = async (): Promise<void> => {
        try {
            const { success, profile, error = '' } = await getHEOnboardingProfile();
            if (success && profile) {
                const { pancard } = profile;
                this.setState({ pancard: pancard?.trim() });
            } else {
                captureErrorString(error);
                track('ERROR', { on: 'getHEOnboardingProfile', what: error });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getHEOnboardingProfile',
                error: (error as Error).message,
            });
        } finally {
            this.setState({ isFetchingProfile: false });
        }
    };

    saveDetailsHandler = async (lastStep: HEOnboardingState, isActivateInGenie?: boolean) => {
        const { pancard } = this.state;
        const { activateInGenie } = this;

        this.setState({
            isSavingProfile: true,
        });

        this.setState({ pancard });
        await setHEOnboardingProfile({ pancard: pancard?.toUpperCase(), lastStep });
        this.setState({
            isSavingProfile: false,
        });
        isActivateInGenie && activateInGenie();
    };

    activateInGenie = async () => {
        try {
            const { profile } = await getHEOnboardingProfile();
            const { isPharmaEnabled, isLabEnabled } = profile;
            await setHEOnboardingProfile({
                isPharmaEnabled,
                isLabEnabled,
                isActivated: true,
                activateInProfile: true,
            });
            track('SET_HE_ONBOARDING_PROFILE', {
                isPharmaEnabled: String(isPharmaEnabled),
                isLabEnabled: String(isLabEnabled),
            });
            notification.success({
                message: 'Health exchange activated successfully!',
                duration: 5,
            });
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'activateInGenie',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error while saving',
                duration: 5,
            });
        }
    };

    onClickHandler = async (skipValidation?: boolean) => {
        const { history } = this.props;
        const { pancard } = this.state;
        const { saveDetailsHandler, currentIndex } = this;

        if (pancard?.length !== 10 && !skipValidation) {
            notification.error({
                message: 'Please enter valid pancard number!',
                duration: 5,
            });
            return;
        }

        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex + 1], true);
            history.replace(GO_TO_HE_ONBOARDING(heOnboardingStepsArray[currentIndex + 1]));
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'setHEOnboardingProfile',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        }
    };

    saveAndExitHandler = async () => {
        const { history } = this.props;
        const { saveDetailsHandler, currentIndex } = this;

        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex]);
            history.replace(GO_TO_APPOINTMENTS(getSelectedClinicId() || ''));
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'setHEOnboardingProfile',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        }
    };

    onBackHandler = (): void => {
        const { history } = this.props;
        const { currentIndex } = this;
        history.replace(GO_TO_HE_ONBOARDING(heOnboardingStepsArray[currentIndex - 1]));
    };

    onChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({
            pancard: e.target.value?.trim(),
        });
    };

    onSkipAndStartHandler = async (): Promise<void> => {
        const { onClickHandler } = this;
        await onClickHandler(true);
    };

    render(): ReactNode {
        const { isFetchingProfile, pancard, isSavingProfile } = this.state;
        const {
            onClickHandler,
            saveAndExitHandler,
            onBackHandler,
            currentIndex,
            onChange,
            onSkipAndStartHandler,
        } = this;

        const disabled = isSavingProfile;

        return (
            <Fragment>
                <FetchingLoader isFetchingProfile={isFetchingProfile} />
                <div className="flex flex-col h-full w-full justify-center">
                    <Header
                        saveAndExitHandler={saveAndExitHandler}
                        disabled={disabled}
                        isSavingProfile={isSavingProfile}
                    />

                    <div className="flex-1 overflow-scroll hide-scrollbar w-592 m-auto pb-32">
                        <div className="space-y-8 mb-24">
                            <p className="text-24 font-700">Add your Pan Card details</p>
                            <p className="text-darwin-neutral-900">
                                Pan Card is required for tax purposes.
                            </p>
                        </div>
                        <div className="space-y-12">
                            <label htmlFor="pancard-input">
                                Pan card number (for tax deductions)
                            </label>
                            <Input
                                value={pancard}
                                onChange={onChange}
                                id="pancard-input"
                                className="uppercase"
                                placeholder="ABCTY1234D"
                            />
                        </div>
                    </div>

                    <Footer
                        onClickHandler={onClickHandler}
                        onBackHandler={onBackHandler}
                        disabled={disabled}
                        isSavingProfile={isSavingProfile}
                        currentIndex={currentIndex}
                        onSkipAndStartHandler={onSkipAndStartHandler}
                        isSkipAndNextLoading={isSavingProfile}
                    />
                </div>
            </Fragment>
        );
    }
}

export default connect()(withRouter(FinishPage));
